import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Database, Zap, Save, AlertTriangle, TrendingUp, X, Package, FileSpreadsheet, RefreshCw, ExternalLink, Trash2, Pencil, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import * as XLSX from "xlsx";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function Masters() {
  const { user } = useAuth();
  const [buyers, setBuyers] = useState([]);
  const [lines, setLines] = useState([]);
  const [styles, setStyles] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("buyer");
  const [form, setForm] = useState({ name: "", code: "", capacity: 0 });
  const [cpm, setCpm] = useState(null);
  const [cpmEdit, setCpmEdit] = useState({ cpm_machine: 0, cpm_table: 0 });
  const [cpmSaving, setCpmSaving] = useState(false);
  const [impactOpen, setImpactOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [cpmHistory, setCpmHistory] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [lookupSaving, setLookupSaving] = useState({});
  const [lookupInputs, setLookupInputs] = useState({ fabric_types: "", fabric_widths: "", tape_items: "", tape_widths: "" });
  // Order (SO) Google Sheet Sync — admin-only config
  const [orderSheet, setOrderSheet] = useState(null);
  const [orderSheetUrl, setOrderSheetUrl] = useState("");
  const [orderSheetSaving, setOrderSheetSaving] = useState(false);
  const [orderSheetSyncing, setOrderSheetSyncing] = useState(false);

  // Buyer Google Sheet Sync — admin-only config
  const [buyerSheet, setBuyerSheet] = useState(null);
  const [buyerSheetUrl, setBuyerSheetUrl] = useState("");
  const [buyerSheetSaving, setBuyerSheetSaving] = useState(false);
  const [buyerSheetSyncing, setBuyerSheetSyncing] = useState(false);

  // Styles bulk upload
  const [stylesUploading, setStylesUploading] = useState(false);
  const [wipeDialogOpen, setWipeDialogOpen] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [replaceMode, setReplaceMode] = useState(false);
  // Line-supervisor dropdown — populated from `/admin/users-by-role`
  const [lineSupervisors, setLineSupervisors] = useState([]);
  // Styles inline edit
  const [styleEdit, setStyleEdit] = useState(null); // full row being edited
  const [styleCreate, setStyleCreate] = useState(null); // blank object when the create dialog is open
  const [styleSaving, setStyleSaving] = useState(false);
  const [styleSearch, setStyleSearch] = useState("");

  // Danger-zone reset dialogs
  const [resetTarget, setResetTarget] = useState(null); // "line" | "bulletin" | null
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const RESET_CONFIRM_WORD = "RESET";
  const runReset = async () => {
    if (resetConfirmText.trim().toUpperCase() !== RESET_CONFIRM_WORD) {
      toast.error(`Type ${RESET_CONFIRM_WORD} to confirm`);
      return;
    }
    setResetBusy(true);
    try {
      if (resetTarget === "line") {
        const { data } = await api.post("/admin/reset/line-tracking");
        toast.success(`Line Tracking cleared — ${data.orders_deleted} SOs, ${data.attendance_deleted} attendance, ${data.output_deleted} output rows removed`);
      } else if (resetTarget === "bulletin") {
        const { data } = await api.post("/admin/reset/bulletins");
        toast.success(`Operation Bulletin cleared — ${data.bulletins_deleted} bulletins removed`);
      }
      setResetTarget(null); setResetConfirmText("");
      await loadOrderSheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
    setResetBusy(false);
  };

  const loadOrderSheet = async () => {
    try {
      const { data } = await api.get("/masters/order-sheet");
      setOrderSheet(data); setOrderSheetUrl(data?.url || "");
    } catch {}
  };
  const saveOrderSheet = async () => {
    setOrderSheetSaving(true);
    try {
      const { data } = await api.put("/masters/order-sheet", { url: orderSheetUrl.trim() });
      setOrderSheet(data);
      toast.success("Order Sheet URL saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setOrderSheetSaving(false);
  };
  const syncOrderSheetNow = async () => {
    if (!(orderSheet?.url || "").trim()) {
      toast.error("Set the Google Sheet URL first");
      return;
    }
    setOrderSheetSyncing(true);
    try {
      const { data } = await api.post("/orders/sync-from-sheet");
      toast.success(`Sync complete — ${data.created} created, ${data.updated} updated${data.skipped_invalid ? `, ${data.skipped_invalid} skipped` : ""}`);
      if (data.qty_manual_kept) {
        const conflicts = data.qty_conflicts || [];
        toast.info(
          `${data.qty_manual_kept} SO(s) kept their admin-set order qty (sheet not applied).` +
          (conflicts.length ? ` Mismatch: ${conflicts.map((c) => `${c.order_number} sheet ${c.sheet_qty} vs manual ${c.manual_qty}`).join("; ")}` : ""),
          { duration: 10000 }
        );
      }
      await loadOrderSheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setOrderSheetSyncing(false);
  };

  const loadBuyerSheet = async () => {
    try {
      const { data } = await api.get("/masters/buyer-sheet");
      setBuyerSheet(data); setBuyerSheetUrl(data?.url || "");
    } catch {}
  };
  const saveBuyerSheet = async () => {
    setBuyerSheetSaving(true);
    try {
      const { data } = await api.put("/masters/buyer-sheet", { url: buyerSheetUrl.trim() });
      setBuyerSheet(data);
      toast.success("Buyer Sheet URL saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBuyerSheetSaving(false);
  };
  const syncBuyerSheetNow = async () => {
    if (!(buyerSheet?.url || "").trim()) { toast.error("Set the Google Sheet URL first"); return; }
    setBuyerSheetSyncing(true);
    try {
      const { data } = await api.post("/masters/buyers/sync-from-sheet");
      const s = data.summary || {};
      toast.success(`Sync complete — ${s.created || 0} new, ${s.skipped_existing || 0} already existed${s.skipped_invalid ? `, ${s.skipped_invalid} skipped` : ""}`);
      await loadBuyerSheet();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setBuyerSheetSyncing(false);
  };

  const deleteLine = async (line) => {
    if (!window.confirm(`Delete "${line.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/masters/lines/${line.id}`);
      toast.success("Line deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const deleteBuyer = async (b) => {
    if (!window.confirm(`Delete buyer "${b.name}"?`)) return;
    try { await api.delete(`/masters/buyers/${b.id}`); toast.success("Buyer deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const deleteStyle = async (s) => {
    if (!window.confirm(`Delete style "${s.our_style}"?`)) return;
    try { await api.delete(`/masters/styles/${s.id}`); toast.success("Style deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const uploadStyles = async (file, replaceMode = false) => {
    if (!file) return;
    setStylesUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = replaceMode ? "/masters/styles/upload?replace_mode=true" : "/masters/styles/upload";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = data.summary || {};
      if (replaceMode) {
        toast.success(`Replaced — wiped ${s.wiped || 0}, imported ${s.created || 0}${s.skipped_invalid ? `, ${s.skipped_invalid} invalid` : ""}`);
      } else {
        toast.success(`Imported — ${s.created || 0} new, ${s.skipped_existing || 0} duplicates skipped${s.skipped_invalid ? `, ${s.skipped_invalid} invalid` : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setStylesUploading(false);
  };

  const wipeAllStyles = async () => {
    try {
      const { data } = await api.delete("/masters/styles/wipe", { data: { confirm: "WIPE STYLES" } });
      toast.success(`Wiped ${data.deleted || 0} styles`);
      setWipeDialogOpen(false);
      setWipeConfirmText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wipe failed");
    }
  };

  const createStyle = async () => {
    if (!styleCreate) return;
    const our_style = (styleCreate.our_style || "").trim();
    if (!our_style) { toast.error("Our Style is required"); return; }
    setStyleSaving(true);
    try {
      await api.post("/masters/styles", {
        our_style,
        name: (styleCreate.name || "").trim(),
        description: (styleCreate.description || "").trim(),
        buyer: (styleCreate.buyer || "").trim(),
        buyer_style: (styleCreate.buyer_style || "").trim(),
      });
      toast.success(`Style ${our_style} created`);
      setStyleCreate(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
    setStyleSaving(false);
  };

  const saveStyleEdit = async () => {
    if (!styleEdit) return;
    const our_style = (styleEdit.our_style || "").trim();
    if (!our_style) { toast.error("Our Style is required"); return; }
    setStyleSaving(true);
    try {
      await api.patch(`/masters/styles/${styleEdit.id}`, {
        our_style,
        name: (styleEdit.name || "").trim(),
        description: (styleEdit.description || "").trim(),
        buyer: (styleEdit.buyer || "").trim(),
        buyer_style: (styleEdit.buyer_style || "").trim(),
      });
      toast.success("Style updated");
      setStyleEdit(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setStyleSaving(false);
  };

  const load = async () => {
    const [b, l, s] = await Promise.all([api.get("/masters/buyers"), api.get("/masters/lines"), api.get("/masters/styles")]);
    setBuyers(b.data); setLines(l.data); setStyles(s.data);
    // Only admins can hit users-by-role; guard so non-admin views don't 403.
    if (isAdmin) {
      try {
        const { data } = await api.get("/admin/users-by-role");
        setLineSupervisors(data.line_supervisor || []);
      } catch { /* non-fatal — dropdown falls back to Other */ }
    }
  };
  const loadCpm = async () => {
    const { data } = await api.get("/masters/cpm");
    setCpm(data); setCpmEdit({ cpm_machine: data.cpm_machine, cpm_table: data.cpm_table });
  };
  const loadCpmHistory = async () => {
    try {
      const { data } = await api.get("/masters/cpm/history");
      const series = (data.series || []).map((p) => ({
        ...p,
        label: p.at ? new Date(p.at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) + " " + new Date(p.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "",
      }));
      setCpmHistory(series);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadCpm(); loadCpmHistory(); loadLookups(); loadOrderSheet(); loadBuyerSheet(); }, []);

  const loadLookups = async () => {
    try { const { data } = await api.get("/masters/enquiry-lookups"); setLookups(data); } catch {}
  };
  const saveLookup = async (key, next) => {
    setLookupSaving((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.patch("/masters/enquiry-lookups", { [key]: next });
      setLookups(data);
      toast.success("Master updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setLookupSaving((s) => ({ ...s, [key]: false }));
  };
  const addLookupItem = (key) => {
    const val = (lookupInputs[key] || "").trim();
    if (!val) return;
    const curr = lookups?.[key] || [];
    if (curr.includes(val)) { toast.info("Already in list"); return; }
    setLookupInputs((s) => ({ ...s, [key]: "" }));
    saveLookup(key, [...curr, val]);
  };
  const removeLookupItem = (key, val) => {
    const curr = lookups?.[key] || [];
    saveLookup(key, curr.filter((v) => v !== val));
  };

  const isAdmin = (user?.role === "admin" || user?.role === "admin_addl");

  const previewCpmImpact = async () => {
    if (!isAdmin) return;
    if (Number(cpmEdit.cpm_machine) === cpm?.cpm_machine && Number(cpmEdit.cpm_table) === cpm?.cpm_table) {
      toast.info("No changes to preview"); return;
    }
    try {
      const { data } = await api.get("/masters/cpm/impact", { params: { cpm_machine: Number(cpmEdit.cpm_machine), cpm_table: Number(cpmEdit.cpm_table) }});
      setImpact(data); setImpactOpen(true);
    } catch { toast.error("Failed to preview impact"); }
  };

  const confirmCpmSave = async () => {
    setCpmSaving(true);
    try {
      const { data } = await api.patch("/masters/cpm", { cpm_machine: Number(cpmEdit.cpm_machine), cpm_table: Number(cpmEdit.cpm_table) });
      setCpm(data); setImpactOpen(false);
      toast.success("CPM Master updated");
      loadCpmHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setCpmSaving(false);
  };

  const add = async () => {
    try {
      if (type === "buyer") await api.post("/masters/buyers", { name: form.name, code: form.code });
      if (type === "line") await api.post("/masters/lines", { name: form.name, capacity: Number(form.capacity), supervisor: form.supervisor || "" });
      toast.success("Added"); setOpen(false); setForm({ name: "", code: "", capacity: 0 }); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="masters-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Database className="h-3.5 w-3.5" /> Masters</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Master Data</h1>
          <p className="text-slate-500 text-[14px] mt-1">CPM rates, buyers, production lines and styles.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="masters-add-btn"><Plus className="h-4 w-4 mr-2" /> Add</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add master</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {["buyer", "line"].map((t) => (
                    <button key={t} onClick={() => setType(t)} className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium border ${type === t ? "bg-brand text-white border-brand" : "border-slate-200"}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                  ))}
                </div>
                <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="master-name" />
                {type === "buyer" && <Input placeholder="Code (e.g. HNM)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="master-code" />}
                {type === "line" && <Input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="master-capacity" />}
                {type === "line" && (
                  <Select value={form.supervisor || ""} onValueChange={(v) => setForm({ ...form, supervisor: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="master-line-supervisor-trigger">
                      <SelectValue placeholder={lineSupervisors.length ? "Pick a Line Supervisor (optional)" : "No line_supervisor users yet — leave blank"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {lineSupervisors.map((u) => (
                        <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={add} className="bg-brand hover:bg-brand-dark text-white">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-5 rounded-md border-slate-200 bg-white" data-testid="cpm-master-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-10 w-10 rounded-md bg-brand text-white flex items-center justify-center shrink-0"><Zap className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-[15px]">CPM Master · Cost Per Minute</div>
          </div>
          {cpm && (
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wide">Machine Op CPM (₹/min)</label>
                <input type="number" step="0.01" disabled={!isAdmin} value={cpmEdit.cpm_machine} onChange={(e) => setCpmEdit({ ...cpmEdit, cpm_machine: e.target.value })} data-testid="master-cpm-machine" className="mt-1 w-32 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-mono font-semibold outline-none focus:border-brand focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50 disabled:cursor-not-allowed" />
              </div>
              <div>
                <label className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wide">Table Op CPM (₹/min)</label>
                <input type="number" step="0.01" disabled={!isAdmin} value={cpmEdit.cpm_table} onChange={(e) => setCpmEdit({ ...cpmEdit, cpm_table: e.target.value })} data-testid="master-cpm-table" className="mt-1 w-32 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-mono font-semibold outline-none focus:border-brand focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50 disabled:cursor-not-allowed" />
              </div>
              {isAdmin && (
                <Button onClick={previewCpmImpact} className="bg-brand hover:bg-brand-dark text-white h-10" data-testid="save-cpm-btn">
                  <Save className="h-4 w-4 mr-2" /> Save Master CPM
                </Button>
              )}
              <div className="text-[10.5px] text-slate-400 font-mono self-center whitespace-nowrap">
                Last updated {cpm.updated_at?.slice(0,10)} by {cpm.updated_by}
              </div>
            </div>
          )}
        </div>

        {/* CPM History Chart */}
        <div className="mt-5 pt-5 border-t border-slate-100" data-testid="cpm-history-chart">
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex items-center gap-1.5 text-[11.5px] font-mono tracking-widest text-slate-500 uppercase">
              <TrendingUp className="h-3.5 w-3.5" /> CPM History
            </div>
            <div className="text-[11px] text-slate-400 font-mono" data-testid="cpm-history-count">
              {cpmHistory.length} {cpmHistory.length === 1 ? "point" : "points"}
            </div>
          </div>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cpmHistory} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={38} tickFormatter={(v) => `₹${v}`} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}
                  formatter={(v, name) => [`₹ ${Number(v).toFixed(2)}`, name === "cpm_machine" ? "Machine Op" : "Table Op"]}
                  labelFormatter={(l) => l}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v === "cpm_machine" ? "Machine Op" : "Table Op"} />
                <Line type="monotone" dataKey="cpm_machine" stroke="#7A1E3A" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="cpm_table" stroke="#0891b2" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* Order (SO) Google Sheet Sync — admin only */}
      {isAdmin && (
        <Card className="p-5 rounded-md border-slate-200 bg-white" data-testid="order-sheet-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0"><FileSpreadsheet className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-[15px]">Order Sheet Sync · Sales Orders</div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Paste the shareable Google Sheet URL (share as <b>Anyone with the link — Viewer</b>). Expected columns:{" "}
                <span className="font-mono">Sales Order #, Buyer Name, Buyer's Style #, BBS Style No, Ord Qty</span> (Delivery Date optional). Auto-syncs every 15 minutes.
              </div>
              <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Input
                  value={orderSheetUrl}
                  onChange={(e) => setOrderSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  data-testid="order-sheet-url"
                  className="h-10 text-[13px] font-mono flex-1"
                />
                <Button
                  onClick={saveOrderSheet}
                  disabled={orderSheetSaving}
                  className="h-10 bg-brand hover:bg-brand-dark text-white"
                  data-testid="order-sheet-save-btn"
                >
                  <Save className="h-4 w-4 mr-2" /> {orderSheetSaving ? "Saving…" : "Save URL"}
                </Button>
                <Button
                  onClick={syncOrderSheetNow}
                  disabled={orderSheetSyncing}
                  variant="outline"
                  className="h-10"
                  data-testid="order-sheet-sync-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${orderSheetSyncing ? "animate-spin" : ""}`} /> {orderSheetSyncing ? "Syncing…" : "Sync Now"}
                </Button>
              </div>
              {orderSheet && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-slate-500 font-mono">
                  {orderSheet.url && (
                    <a href={orderSheet.url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="order-sheet-open-link">
                      <ExternalLink className="h-3 w-3" /> Open sheet
                    </a>
                  )}
                  {orderSheet.last_sync_at ? (
                    <span data-testid="order-sheet-last-sync">
                      Last sync: <span className="text-slate-700 font-semibold">{new Date(orderSheet.last_sync_at).toLocaleString()}</span>
                      {orderSheet.last_sync_by && <> · by {orderSheet.last_sync_by}</>}
                      {orderSheet.last_sync_summary && (
                        <> · created {orderSheet.last_sync_summary.created || 0}, updated {orderSheet.last_sync_summary.updated || 0}
                          {typeof orderSheet.last_sync_summary.skipped_invalid === "number" && orderSheet.last_sync_summary.skipped_invalid > 0 && <>, skipped {orderSheet.last_sync_summary.skipped_invalid}</>}
                        </>
                      )}
                    </span>
                  ) : (
                    <span>Not synced yet</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
      <Card className="p-5 rounded-md border-slate-200 bg-white" data-testid="enquiry-lookups-card">
        <div className="flex items-start gap-4 mb-4">
          <div className="h-10 w-10 rounded-md bg-brand text-white flex items-center justify-center shrink-0"><Package className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-[15px]">Enquiry Costing Lookups</div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              These lists power the dropdowns inside Enquiry Costing (Fabric Type, Fabric Width, Tape/Elastic Item, Tape/Elastic Width). {isAdmin ? "Admins only." : "Read-only · admin managed."}
            </div>
          </div>
        </div>
        {lookups && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "fabric_types",  title: "Fabric Type",   ph: "e.g. Main-C" },
              { key: "fabric_widths", title: "Fabric Width",  ph: 'e.g. 48"' },
              { key: "tape_items",    title: "Tape / Elastic Item",  ph: "e.g. Bias Tape" },
              { key: "tape_widths",   title: "Tape / Elastic Width", ph: "e.g. 15 MM" },
            ].map(({ key, title, ph }) => (
              <div key={key} className="rounded-md border border-slate-200 p-3" data-testid={`lookup-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11.5px] font-mono tracking-widest text-slate-500 uppercase">{title}</div>
                  <div className="text-[10.5px] text-slate-400 font-mono">{(lookups[key] || []).length}</div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2 min-h-[36px]">
                  {(lookups[key] || []).map((v) => (
                    <span key={v} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-brand-soft text-brand text-[12px] font-medium" data-testid={`lookup-item-${key}-${v}`}>
                      {v}
                      {isAdmin && (
                        <button
                          onClick={() => removeLookupItem(key, v)}
                          disabled={!!lookupSaving[key]}
                          className="opacity-60 hover:opacity-100 hover:text-rose-700 transition"
                          data-testid={`lookup-remove-${key}-${v}`}
                          aria-label={`Remove ${v}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {!lookups[key]?.length && <span className="text-[11.5px] text-slate-400 italic self-center">(empty)</span>}
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Input
                      value={lookupInputs[key]}
                      onChange={(e) => setLookupInputs((s) => ({ ...s, [key]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLookupItem(key); } }}
                      placeholder={ph}
                      data-testid={`lookup-input-${key}`}
                      className="h-9 text-[13px]"
                    />
                    <Button
                      onClick={() => addLookupItem(key)}
                      disabled={!!lookupSaving[key] || !lookupInputs[key]?.trim()}
                      className="h-9 bg-brand hover:bg-brand-dark text-white"
                      data-testid={`lookup-add-${key}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={impactOpen} onOpenChange={setImpactOpen}>
        <DialogContent data-testid="cpm-impact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Confirm CPM Master Change</DialogTitle>
          </DialogHeader>
          {impact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Machine Op CPM</div>
                  <div className="mt-1 font-mono text-[15px]">
                    <span className="text-slate-500 line-through">₹ {cpm?.cpm_machine}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="text-emerald-700 font-bold">₹ {impact.new_cpm.cpm_machine}</span>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Table Op CPM</div>
                  <div className="mt-1 font-mono text-[15px]">
                    <span className="text-slate-500 line-through">₹ {cpm?.cpm_table}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="text-emerald-700 font-bold">₹ {impact.new_cpm.cpm_table}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-[13px] text-amber-900 space-y-1.5">
                <div className="font-heading font-semibold text-[14px]">Impact preview</div>
                <div>All <span className="font-bold">new bulletins</span> going forward will use the new rates.</div>
                <div>Existing bulletins that will show a CPM-drift alert on their next version: <span className="font-bold" data-testid="impact-drift-total">{impact.would_drift_total}</span> of {impact.total_bulletins} total.</div>
                <div className="text-[12px]">Of these, <span className="font-semibold" data-testid="impact-drift-approved">{impact.would_drift_approved}</span> are currently Approved — their frozen rates remain until a new version is created.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpactOpen(false)}>Cancel</Button>
            <Button onClick={confirmCpmSave} disabled={cpmSaving} className="bg-brand hover:bg-brand-dark text-white" data-testid="confirm-cpm-save-btn">
              <Save className="h-4 w-4 mr-2" /> {cpmSaving ? "Saving…" : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="buyers">
        <TabsList>
          <TabsTrigger value="buyers" data-testid="tab-buyers">Buyers ({buyers.length})</TabsTrigger>
          <TabsTrigger value="lines" data-testid="tab-lines">Production Lines ({lines.length})</TabsTrigger>
          <TabsTrigger value="styles" data-testid="tab-styles">Styles ({styles.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="buyers">
          <div className="space-y-4">
            {isAdmin && (
              <Card className="p-4 rounded-md border-slate-200 bg-white" data-testid="buyer-sheet-card">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileSpreadsheet className="h-4 w-4 text-brand" />
                  <div className="font-heading font-semibold text-[14px]">Buyers · Google Sheet Sync</div>
                </div>
                <div className="text-[12px] text-slate-500 mb-3">
                  Paste a Google Sheet share link. The sheet must have a <span className="font-mono">Buyer / Name</span> column (and optional <span className="font-mono">Code</span>). Existing buyers are never overwritten — duplicates are skipped by name (case-insensitive).
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[240px]">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Sheet URL</label>
                    <Input value={buyerSheetUrl} onChange={(e) => setBuyerSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" data-testid="buyer-sheet-url" />
                  </div>
                  <Button variant="outline" onClick={saveBuyerSheet} disabled={buyerSheetSaving} data-testid="buyer-sheet-save">
                    {buyerSheetSaving ? "Saving…" : (<><Save className="h-4 w-4 mr-1" /> Save</>)}
                  </Button>
                  <Button className="bg-brand hover:bg-brand-dark text-white" onClick={syncBuyerSheetNow} disabled={buyerSheetSyncing || !(buyerSheet?.url || "").trim()} data-testid="buyer-sheet-sync">
                    {buyerSheetSyncing ? "Syncing…" : (<><RefreshCw className="h-4 w-4 mr-1" /> Sync now</>)}
                  </Button>
                  {(buyerSheet?.url || "").trim() && (
                    <a href={buyerSheet.url} target="_blank" rel="noreferrer" className="text-[12px] text-slate-500 hover:text-brand inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Open sheet</a>
                  )}
                </div>
                {buyerSheet?.last_sync_at && (
                  <div className="mt-2 text-[11.5px] text-slate-500">
                    Last synced {new Date(buyerSheet.last_sync_at).toLocaleString()} by {buyerSheet.last_sync_by}
                    {buyerSheet.last_sync_summary && (
                      <> — <span className="font-mono">{buyerSheet.last_sync_summary.created || 0} new · {buyerSheet.last_sync_summary.skipped_existing || 0} existing · {buyerSheet.last_sync_summary.skipped_invalid || 0} invalid</span></>
                    )}
                  </div>
                )}
              </Card>
            )}
            <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="opsiq-table">
                <thead><tr><th>Name</th><th>Code</th><th>Source</th><th>Created</th>{isAdmin && <th className="w-16"></th>}</tr></thead>
                <tbody>
                  {buyers.map((b) => (
                    <tr key={b.id} data-testid={`buyer-row-${b.id}`}>
                      <td className="font-semibold">{b.name}</td>
                      <td className="font-mono">{b.code}</td>
                      <td className="text-[11.5px]"><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10.5px] uppercase tracking-wider ${b.source === "sheet" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{b.source || "manual"}</span></td>
                      <td className="text-slate-500 text-[12.5px]">{b.created_at?.slice(0,10)}</td>
                      {isAdmin && (
                        <td className="text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteBuyer(b)} data-testid={`buyer-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {buyers.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-slate-400 text-[13px]">No buyers yet — sync from Google Sheet or add manually.</td></tr>}
                </tbody>
              </table>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="lines">
          <div className="space-y-3">
            {isAdmin && (
              <div className="flex justify-end">
                <Button size="sm" className="bg-brand text-white hover:bg-brand-dark" onClick={() => { setType("line"); setForm({ name: "", code: "", capacity: 0 }); setOpen(true); }} data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1" /> Add production line</Button>
              </div>
            )}
            <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="opsiq-table">
                <thead><tr><th>Line</th><th>Supervisor</th><th className="text-right" title="Daily capacity in pcs — used by the shared-line output alert to avoid false positives when multiple SOs share a line. 0 = no cap.">Capacity (pcs/day)</th><th>Created</th>{isAdmin && <th className="w-16"></th>}</tr></thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.id} data-testid={`line-row-${l.id}`}>
                      <td className="font-semibold">{l.name}</td>
                      <td className="text-[12.5px]">
                        {isAdmin ? (
                          (() => {
                            const cur = (l.supervisor || "").trim();
                            const supNames = lineSupervisors.map((u) => u.name);
                            const inList = cur && supNames.some((n) => n.toLowerCase() === cur.toLowerCase());
                            const selValue = !cur ? "" : (inList ? cur : "__custom__");
                            const patch = async (val) => {
                              try {
                                await api.patch(`/masters/lines/${l.id}`, { supervisor: val });
                                toast.success(`Supervisor updated for ${l.name}`);
                                await load();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Update failed");
                              }
                            };
                            return (
                              <>
                                <Select
                                  value={selValue}
                                  onValueChange={(v) => {
                                    if (v === "__custom__") {
                                      // stay on custom mode; the Input below will drive the save
                                      return;
                                    }
                                    if (v === "__clear__") {
                                      patch("");
                                      return;
                                    }
                                    patch(v);
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-[12.5px]" data-testid={`line-supervisor-trigger-${l.id}`}>
                                    <SelectValue placeholder={lineSupervisors.length ? "Pick a Line Supervisor…" : "No line_supervisor users yet"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {lineSupervisors.map((u) => (
                                      <SelectItem key={u.email} value={u.name} data-testid={`line-supervisor-opt-${l.id}-${u.email}`}>
                                        {u.name} <span className="text-slate-400 font-mono text-[10.5px] ml-1">· {u.email}</span>
                                      </SelectItem>
                                    ))}
                                    <SelectItem value="__custom__" data-testid={`line-supervisor-opt-custom-${l.id}`}>＋ Other (type below)</SelectItem>
                                    {cur && <SelectItem value="__clear__" data-testid={`line-supervisor-opt-clear-${l.id}`}>✕ Clear supervisor</SelectItem>}
                                  </SelectContent>
                                </Select>
                                {(selValue === "__custom__" || (cur && !inList)) && (
                                  <Input
                                    defaultValue={cur}
                                    placeholder="Type name (not in Line Supervisor role list)"
                                    className="h-8 text-[12.5px] mt-1"
                                    onBlur={(e) => {
                                      const v = e.target.value.trim();
                                      if (v !== cur) patch(v);
                                    }}
                                    data-testid={`line-supervisor-custom-${l.id}`}
                                  />
                                )}
                                {cur && !inList && (
                                  <div className="text-[10.5px] text-amber-700 mt-0.5">Not a Line Supervisor role — free-text kept.</div>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <span className="text-slate-700">{l.supervisor || <span className="text-slate-400 italic">—</span>}</span>
                        )}
                      </td>
                      <td className="text-right font-mono">
                        {isAdmin ? (
                          <Input
                            type="number"
                            min={0}
                            defaultValue={l.capacity ?? 0}
                            className="h-8 text-[12.5px] text-right font-mono w-24 ml-auto"
                            title="Daily capacity in pcs — used by the shared-line output alert to avoid false positives when multiple SOs run on the same line."
                            data-testid={`line-capacity-input-${l.id}`}
                            onBlur={async (e) => {
                              const val = Number(e.target.value || 0);
                              if (val === (l.capacity ?? 0)) return;
                              try {
                                await api.patch(`/masters/lines/${l.id}`, { capacity: val });
                                toast.success(`Capacity updated for ${l.name}`);
                                await load();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Update failed");
                              }
                            }}
                          />
                        ) : (l.capacity ?? 0)}
                      </td>
                      <td className="text-slate-500 text-[12.5px]">{l.created_at?.slice(0,10)}</td>
                      {isAdmin && (
                        <td className="text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteLine(l)} data-testid={`line-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {lines.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-slate-400 text-[13px]">No production lines yet — click Add production line to create one.</td></tr>}
                </tbody>
              </table>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="styles">
          <div className="space-y-3">
            {isAdmin && (
              <Card className="p-4 rounded-md border-slate-200 bg-white" data-testid="styles-upload-card">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileSpreadsheet className="h-4 w-4 text-brand" />
                  <div className="font-heading font-semibold text-[14px]">Styles · Bulk Upload</div>
                </div>
                <div className="text-[12px] text-slate-500 mb-3">
                  Upload an Excel <span className="font-mono">.xlsx</span> or CSV. Columns (case-insensitive):
                  <span className="font-mono ml-1">Our Style</span> (required),
                  <span className="font-mono ml-1">Buyer Style</span>,
                  <span className="font-mono ml-1">Product Group</span>,
                  <span className="font-mono ml-1">Description</span>,
                  <span className="font-mono ml-1">Buyer</span>. Rows whose Our Style already exists are automatically skipped.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStyleCreate({ our_style: "", buyer_style: "", name: "", description: "", buyer: "" })}
                    data-testid="styles-create-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-brand hover:bg-brand-dark text-white text-[13px] font-semibold"
                  >
                    <Plus className="h-4 w-4" /> Create style
                  </button>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { uploadStyles(e.target.files?.[0], replaceMode); e.target.value = ""; }} data-testid="styles-upload-input" />
                    <span className={`inline-flex items-center h-9 px-3 rounded-md text-[13px] font-semibold border ${replaceMode ? "border-rose-300 text-rose-700 bg-white hover:bg-rose-50" : "border-brand/40 text-brand bg-white hover:bg-brand-soft"}`}>
                      {stylesUploading ? "Uploading…" : (<><Plus className="h-4 w-4 mr-1" /> {replaceMode ? "Choose file (wipe & replace)" : "Bulk upload file"}</>)}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.get("/masters/styles/template", { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url; a.download = "opsiq-styles-template.xlsx"; a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 500);
                      } catch {
                        toast.error("Template download failed");
                      }
                    }}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 text-[12.5px] font-semibold"
                    data-testid="styles-template-download"
                  >
                    <FileSpreadsheet className="h-4 w-4" /> Download blank template
                  </button>
                  <label className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600 select-none">
                    <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} data-testid="styles-replace-mode" />
                    <span>Replace-all mode <span className="text-[11px] text-rose-600">(wipes existing before import)</span></span>
                  </label>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => { setWipeConfirmText(""); setWipeDialogOpen(true); }}
                    data-testid="styles-wipe-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 text-[12.5px] font-semibold"
                  >
                    <Trash2 className="h-4 w-4" /> Wipe all styles
                  </button>
                </div>
              </Card>
            )}
            <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search Our Style, Buyer Style, Product Group, description, buyer…"
                    value={styleSearch}
                    onChange={(e) => setStyleSearch(e.target.value)}
                    className="h-9 pl-8 text-[13px]"
                    data-testid="styles-master-search"
                  />
                </div>
                <div className="text-[11.5px] text-slate-500 font-mono">
                  {(() => {
                    const q = styleSearch.trim().toLowerCase();
                    const total = styles.length;
                    if (!q) return `${total} style(s)`;
                    const shown = styles.filter((s) => {
                      const pool = [s.our_style, s.buyer_style, s.name, s.description, s.buyer].filter(Boolean).map((v) => String(v).toLowerCase());
                      return pool.some((p) => p.includes(q));
                    }).length;
                    return `${shown} / ${total} match`;
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Export what's currently visible (honors the search filter). If no filter,
                    // exports every style. File-name reflects the filter for easy triage.
                    const q = styleSearch.trim().toLowerCase();
                    const rows = q
                      ? styles.filter((s) => {
                          const pool = [s.our_style, s.buyer_style, s.name, s.description, s.buyer].filter(Boolean).map((v) => String(v).toLowerCase());
                          return pool.some((p) => p.includes(q));
                        })
                      : styles;
                    if (rows.length === 0) { toast.error("Nothing to export"); return; }
                    const data = rows.map((s) => ({
                      "Our Style": s.our_style || "",
                      "Buyer Style": s.buyer_style || "",
                      "Product Group": s.name || "",
                      "Description": s.description || "",
                      "Buyer": s.buyer || "",
                      "Source": s.source || "manual",
                    }));
                    const ws = XLSX.utils.json_to_sheet(data);
                    ws["!cols"] = [{ wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 40 }, { wch: 20 }, { wch: 10 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Styles");
                    const today = new Date().toISOString().slice(0, 10);
                    const suffix = q ? `filtered-${q.slice(0, 20).replace(/[^a-z0-9]+/gi, "_")}` : "all";
                    XLSX.writeFile(wb, `opsiq-styles-${suffix}-${today}.xlsx`);
                    toast.success(`Exported ${rows.length} style(s)`);
                  }}
                  disabled={styles.length === 0}
                  data-testid="styles-export-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed text-[12.5px] font-semibold"
                >
                  <Download className="h-4 w-4" /> Download Excel
                </button>
              </div>
              <table className="opsiq-table" style={{ tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th style={{ width: "13%" }}>Our Style</th>
                    <th style={{ width: "13%" }}>Buyer Style</th>
                    <th style={{ width: "14%" }}>Product Group</th>
                    <th>Description</th>
                    <th style={{ width: "12%" }}>Buyer</th>
                    <th style={{ width: "9%" }}>Source</th>
                    {isAdmin && <th className="w-16"></th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const q = styleSearch.trim().toLowerCase();
                    const rows = q
                      ? styles.filter((s) => {
                          const pool = [s.our_style, s.buyer_style, s.name, s.description, s.buyer].filter(Boolean).map((v) => String(v).toLowerCase());
                          return pool.some((p) => p.includes(q));
                        })
                      : styles;
                    return rows.map((s) => (
                    <tr key={s.id} data-testid={`style-row-${s.id}`}>
                      <td className="font-mono font-semibold text-brand break-words">{s.our_style}</td>
                      <td className="font-mono text-[12.5px] text-slate-700 break-words">{s.buyer_style || ""}</td>
                      <td className="break-words">{s.name}</td>
                      <td className="text-[12.5px] text-slate-600" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{s.description || ""}</td>
                      <td className="break-words">{s.buyer}</td>
                      <td className="text-[11.5px]"><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10.5px] uppercase tracking-wider ${s.source === "upload" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{s.source || "manual"}</span></td>
                      {isAdmin && (
                        <td className="text-right">
                          <div className="inline-flex items-center gap-0.5">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-600 hover:text-brand hover:bg-slate-50" onClick={() => setStyleEdit({ ...s })} data-testid={`style-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteStyle(s)} data-testid={`style-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ));
                  })()}
                  {styles.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-400 text-[13px]">No styles yet — use Create style or bulk-upload an Excel file above.</td></tr>}
                  {styleSearch.trim() && styles.length > 0 && styles.filter((s) => {
                    const q = styleSearch.trim().toLowerCase();
                    const pool = [s.our_style, s.buyer_style, s.name, s.description, s.buyer].filter(Boolean).map((v) => String(v).toLowerCase());
                    return pool.some((p) => p.includes(q));
                  }).length === 0 && (
                    <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-400 text-[13px]">No styles match "{styleSearch}".</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Style edit dialog */}
      <Dialog open={!!styleEdit} onOpenChange={(o) => { if (!o) setStyleEdit(null); }}>
        <DialogContent data-testid="style-edit-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-brand" /> Edit style</DialogTitle>
          </DialogHeader>
          {styleEdit && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Our Style *</label>
                <Input value={styleEdit.our_style || ""} onChange={(e) => setStyleEdit({ ...styleEdit, our_style: e.target.value })} className="mt-1 font-mono" data-testid="style-edit-our-style" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Buyer Style</label>
                <Input value={styleEdit.buyer_style || ""} onChange={(e) => setStyleEdit({ ...styleEdit, buyer_style: e.target.value })} placeholder="e.g. HM-24-POLO-BLU" className="mt-1 font-mono" data-testid="style-edit-buyer-style" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Product Group</label>
                <Input value={styleEdit.name || ""} onChange={(e) => setStyleEdit({ ...styleEdit, name: e.target.value })} placeholder="e.g. Polo T-Shirt" className="mt-1" data-testid="style-edit-name" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Description</label>
                <Textarea rows={4} value={styleEdit.description || ""} onChange={(e) => setStyleEdit({ ...styleEdit, description: e.target.value })} placeholder="Longer description of the style, materials, construction highlights…" className="mt-1" data-testid="style-edit-description" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Buyer</label>
                {(() => {
                  const bNames = buyers.map((b) => b.name);
                  const current = (styleEdit.buyer || "").trim();
                  const inMaster = current && bNames.some((n) => n.toLowerCase() === current.toLowerCase());
                  // Sentinel value so the Select can render an empty/custom state without
                  // firing an accidental change. `__custom__` = "let me type a new buyer".
                  const selValue = !current ? "" : (inMaster ? current : "__custom__");
                  return (
                    <>
                      <Select
                        value={selValue}
                        onValueChange={(v) => {
                          if (v === "__custom__") {
                            // Preserve whatever is currently typed; if empty, seed with the row's existing value.
                            setStyleEdit({ ...styleEdit, buyer: current });
                          } else {
                            setStyleEdit({ ...styleEdit, buyer: v });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid="style-edit-buyer-trigger">
                          <SelectValue placeholder={buyers.length ? "Pick a buyer from the master…" : "No buyers in master yet"} />
                        </SelectTrigger>
                        <SelectContent>
                          {buyers.map((b) => (
                            <SelectItem key={b.id} value={b.name} data-testid={`style-edit-buyer-option-${b.id}`}>{b.name}{b.code ? ` (${b.code})` : ""}</SelectItem>
                          ))}
                          <SelectItem value="__custom__" data-testid="style-edit-buyer-option-custom">＋ Other (type below)</SelectItem>
                        </SelectContent>
                      </Select>
                      {(selValue === "__custom__" || (current && !inMaster)) && (
                        <Input
                          value={styleEdit.buyer || ""}
                          onChange={(e) => setStyleEdit({ ...styleEdit, buyer: e.target.value })}
                          placeholder="Type buyer name (not in master)"
                          className="mt-2"
                          data-testid="style-edit-buyer-custom"
                        />
                      )}
                      {current && !inMaster && (
                        <div className="text-[11px] text-amber-700 mt-1">Buyer "{current}" isn't in the Buyers master yet — you can still save it as free text, or add it under the Buyers tab first.</div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStyleEdit(null)} disabled={styleSaving} data-testid="style-edit-cancel">Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={saveStyleEdit} disabled={styleSaving} data-testid="style-edit-save">
              {styleSaving ? "Saving…" : (<><Save className="h-4 w-4 mr-1" /> Save</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create style — one-by-one entry (admin only) */}
      <Dialog open={!!styleCreate} onOpenChange={(o) => { if (!o) setStyleCreate(null); }}>
        <DialogContent data-testid="style-create-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-brand" /> Create style</DialogTitle>
          </DialogHeader>
          {styleCreate && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Our Style *</label>
                <Input value={styleCreate.our_style} onChange={(e) => setStyleCreate({ ...styleCreate, our_style: e.target.value })} className="mt-1 font-mono" placeholder="e.g. 07/NAV/377" data-testid="style-create-our-style" autoFocus />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Buyer Style</label>
                <Input value={styleCreate.buyer_style} onChange={(e) => setStyleCreate({ ...styleCreate, buyer_style: e.target.value })} placeholder="e.g. HM-24-POLO-BLU" className="mt-1 font-mono" data-testid="style-create-buyer-style" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Product Group</label>
                <Input value={styleCreate.name} onChange={(e) => setStyleCreate({ ...styleCreate, name: e.target.value })} placeholder="e.g. Polo T-Shirt" className="mt-1" data-testid="style-create-name" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Description</label>
                <Textarea rows={4} value={styleCreate.description} onChange={(e) => setStyleCreate({ ...styleCreate, description: e.target.value })} placeholder="Longer description of the style, materials, construction highlights…" className="mt-1" data-testid="style-create-description" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Buyer</label>
                {(() => {
                  const bNames = buyers.map((b) => b.name);
                  const current = (styleCreate.buyer || "").trim();
                  const inMaster = current && bNames.some((n) => n.toLowerCase() === current.toLowerCase());
                  const selValue = !current ? "" : (inMaster ? current : "__custom__");
                  return (
                    <>
                      <Select
                        value={selValue}
                        onValueChange={(v) => setStyleCreate({ ...styleCreate, buyer: v === "__custom__" ? current : v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="style-create-buyer-trigger">
                          <SelectValue placeholder={buyers.length ? "Pick a buyer from the master…" : "No buyers in master yet"} />
                        </SelectTrigger>
                        <SelectContent>
                          {buyers.map((b) => (
                            <SelectItem key={b.id} value={b.name} data-testid={`style-create-buyer-option-${b.id}`}>{b.name}{b.code ? ` (${b.code})` : ""}</SelectItem>
                          ))}
                          <SelectItem value="__custom__" data-testid="style-create-buyer-option-custom">＋ Other (type below)</SelectItem>
                        </SelectContent>
                      </Select>
                      {selValue === "__custom__" && (
                        <Input
                          value={styleCreate.buyer || ""}
                          onChange={(e) => setStyleCreate({ ...styleCreate, buyer: e.target.value })}
                          placeholder="Type buyer name (not in master)"
                          className="mt-2"
                          data-testid="style-create-buyer-custom"
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStyleCreate(null)} disabled={styleSaving} data-testid="style-create-cancel">Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={createStyle} disabled={styleSaving} data-testid="style-create-save">
              {styleSaving ? "Creating…" : (<><Save className="h-4 w-4 mr-1" /> Create</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger Zone — admin only */}
      {isAdmin && (
        <Card className="p-5 rounded-md border border-red-200 bg-red-50/40" data-testid="danger-zone-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-md bg-red-600 text-white flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-[15px] text-red-900">Danger Zone · Module Resets</div>
              <div className="text-[12.5px] text-red-900/70 mt-0.5">
                Irreversibly wipes all records inside a module. Users, roles, email config, enquiries, masters and all other modules are left untouched. Use only when starting a module fresh (e.g. going live).
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border border-red-200 bg-white p-3">
                  <div className="font-semibold text-[13.5px] text-red-900">Reset Line Tracking</div>
                  <div className="text-[11.5px] text-slate-600 mt-0.5">Deletes all Sales Orders, Attendance and Output entries. The SO Sheet URL stays saved.</div>
                  <Button
                    onClick={() => { setResetTarget("line"); setResetConfirmText(""); }}
                    className="mt-3 h-10 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="reset-line-tracking-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Clear all Line-Tracking data
                  </Button>
                </div>
                <div className="rounded-md border border-red-200 bg-white p-3">
                  <div className="font-semibold text-[13.5px] text-red-900">Reset Operation Bulletin</div>
                  <div className="text-[11.5px] text-slate-600 mt-0.5">Deletes every Operation Bulletin (all versions, all stages). Machines/CPM masters remain.</div>
                  <Button
                    onClick={() => { setResetTarget("bulletin"); setResetConfirmText(""); }}
                    className="mt-3 h-10 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="reset-bulletins-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Clear all Operation Bulletins
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetConfirmText(""); } }}>
        <DialogContent data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Confirm {resetTarget === "line" ? "Line Tracking" : "Operation Bulletin"} reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-[13px] text-red-900">
              This will <b>permanently delete</b>{" "}
              {resetTarget === "line" ? (
                <>every synced Sales Order, every Attendance row and every Output entry.</>
              ) : (
                <>every Operation Bulletin — all versions, all stages, all approvals.</>
              )}{" "}
              This action cannot be undone.
            </div>
            <div>
              <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Type <span className="font-mono text-red-700">RESET</span> to confirm</label>
              <Input
                autoFocus
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                className="mt-1 h-10 font-mono"
                data-testid="reset-confirm-input"
                placeholder="RESET"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetConfirmText(""); }} data-testid="reset-cancel-btn">Cancel</Button>
            <Button
              onClick={runReset}
              disabled={resetBusy || resetConfirmText.trim().toUpperCase() !== "RESET"}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reset-confirm-btn"
            >
              {resetBusy ? "Deleting…" : "Yes, delete everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe-all-styles confirmation modal */}
      <Dialog open={wipeDialogOpen} onOpenChange={(v) => { if (!v) { setWipeDialogOpen(false); setWipeConfirmText(""); } }}>
        <DialogContent data-testid="styles-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Wipe ALL styles
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px]">
            <p className="text-slate-700">This deletes <b>every row</b> from the Styles master ({styles.length} styles). This cannot be undone.</p>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[12.5px] text-amber-900">
              Bulletins, enquiries and samples that reference styles by <span className="font-mono">our_style</span> stay intact — they'll re-link automatically if you re-import with the same identifiers.
            </div>
            <div>
              <label className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-600">Type <span className="font-mono text-rose-700">WIPE STYLES</span> to confirm</label>
              <Input value={wipeConfirmText} onChange={(e) => setWipeConfirmText(e.target.value)} className="mt-1 font-mono" placeholder="WIPE STYLES" data-testid="styles-wipe-confirm-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setWipeDialogOpen(false); setWipeConfirmText(""); }} data-testid="styles-wipe-cancel">Cancel</Button>
            <Button
              onClick={wipeAllStyles}
              disabled={wipeConfirmText !== "WIPE STYLES"}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
              data-testid="styles-wipe-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Yes, wipe everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
